import { supabase } from '../config/supabaseClient';
import ENV from '../config/env';

const buildUrl = (path: string) => {
  const base = (ENV.API_URL || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
};

const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  const data: any = sessionResp?.data;
  return data?.session?.access_token || '';
};

async function fetchJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const res = await fetch(url, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(init?.headers || {})
    }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export type QRCreateOptions = {
  original_url: string;
  custom_code?: string;
  title?: string;
  qr_options?: {
    size?: number;
    margin?: number;
    fg_color?: string;
    bg_color?: string;
    logo_url?: string;
  };
};

export type QRCreateResponse = {
  id: string;
  was_shortened: boolean;
  short_url: string | null;
  original_url: string;
  preview_url: string | null;
  storage_path: string;
  short_code: string | null;
};

export type QRListItem = {
  id: string;
  user_id: string;
  shortened_url_id: string | null;
  original_url: string;
  title: string | null;
  format: 'png';
  size: number;
  margin: number;
  fg_color: string;
  bg_color: string;
  logo_url: string | null;
  storage_path: string;
  created_at: string;
};

const qrService = {
  async create(input: QRCreateOptions): Promise<QRCreateResponse> {
    if (!input?.original_url) throw new Error('original_url is required');
    return fetchJSON(buildUrl('/api/qr'), {
      method: 'POST',
      body: JSON.stringify(input)
    });
  },

  async list(limit = 20, offset = 0): Promise<{ items: QRListItem[]; limit: number; offset: number; }> {
    const url = buildUrl(`/api/qr?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`);
    return fetchJSON(url, { method: 'GET' });
  },

  async getImageUrl(id: string): Promise<{ url: string }> {
    if (!id) throw new Error('id is required');
    return fetchJSON(buildUrl(`/api/qr/${id}/image`), { method: 'GET' });
  }
};

export default qrService;
